// src/pages/PrivacyPolicy.tsx
import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy(): JSX.Element {
  return (
    <Box 
      sx={{ 
        fontFamily: 'sans-serif', 
        lineHeight: 1.6, 
        p: { xs: 2, sm: 3, md: 4 }, 
        maxWidth: '800px', 
        mx: 'auto' 
      }}
    >
      {/* Set the page title */}
      <Helmet>
        <title>Privacy Policy</title>
      </Helmet>

      <Typography variant="h4" component="h1" gutterBottom>
        Privacy Policy for CityWise
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>Effective Date:</strong> October 2nd 2025
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        1. Introduction
      </Typography>
      <Typography variant="body1" paragraph>
        Welcome to CityWise. We are committed to protecting your privacy and handling your data in an open and transparent manner.
      </Typography>
      <Typography variant="body1" paragraph>
        This privacy policy outlines how CityWise collects, uses, maintains, and discloses your personal information when you use our website and services. As a company based in Sweden, we are committed to protecting your privacy and handling your data in an open and transparent manner, in full compliance with the EU's General Data Protection Regulation (GDPR).
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        2. Information We Collect
      </Typography>
      <Typography variant="body1" paragraph>
        We adhere to the principle of data minimization, which means we only collect the personal information that is absolutely necessary to provide you with our services. The information we collect falls into two categories:
      </Typography>
      <ul>
        <li>
          <Typography variant="body1">
            <strong>Account Information:</strong> When you register for an account, we collect your username and email address. This information is required to create your account, secure your access to the service, and communicate with you about important account-related matters.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>Feedback and Communications:</strong> When you voluntarily send us feedback, support requests, or other communications through our feedback forms or via email, we collect the information you provide in those communications. This may include your email address and the content of your message.
          </Typography>
        </li>
      </ul>
      <Typography variant="body1" paragraph>
        We do not collect any other personal information, such as usage data, analytics, or tracking information.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        3. How We Use Your Information
      </Typography>
      <Typography variant="body1" paragraph>
        We use the information we collect solely for the following purposes:
      </Typography>
      <ul>
        <li>
          <Typography variant="body1">
            <strong>To Provide and Maintain Our Service:</strong> We use your account information to manage your account, provide you with access to our services, and authenticate you as a user.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>To Communicate With You:</strong> We use your email address to send important service-related notifications, such as password resets, security alerts, and system maintenance updates, based on the notification preferences you select in your settings.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>To Respond to and Improve Our Service:</strong> We use the information from your feedback and support requests to respond to your inquiries and to improve the functionality and user experience of CityWise.
          </Typography>
        </li>
      </ul>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        4. Data Storage and Security
      </Typography>
      <Typography variant="body1" paragraph>
        We take the security of your data very seriously. We implement a variety of security measures to maintain the safety of your personal information when you enter, submit, or access it. Your account information, including your password, is stored in an encrypted format. All data is stored on secure servers.
      </Typography>
      
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        5. Data Retention
      </Typography>
      <Typography variant="body1" paragraph>
        We will retain your personal information only for as long as is necessary for the purposes set out in this privacy policy. We will retain your account information for as long as your account is active. If you delete your account, we will also delete this personal information.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        6. Sharing of Your Information
      </Typography>
      <Typography variant="body1" paragraph>
        We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties. We may release your information when we believe release is appropriate to comply with the law, enforce our site policies, or protect ours or others' rights, property, or safety.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        7. Your Data Protection Rights
      </Typography>
      <Typography variant="body1" paragraph>
        You have the right to access, rectify, or request erasure of your personal data. You can update your account information at any time in the "Account" section of the settings. For other requests, please contact us.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        8. Changes to This Privacy Policy
      </Typography>
      <Typography variant="body1" paragraph>
        We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        9. Contact Us
      </Typography>
      <Typography variant="body1" paragraph>
        If you have any questions about this Privacy Policy, please contact us by email: <a href="mailto:contact@citywisestrategy.com">contact@citywisestrategy.com</a>
       </Typography>
    </Box>
  );
}